import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NotificationService } from '../../../../../shared/services/notification.service';
import {
  LocationPaymentsService,
  StripeStatusDto,
  PaymentSettingsDto
} from '../../../services/location-payments.service';

/**
 * "Pagos" section of the location settings form. Composes two blocks:
 *  1) Stripe Connect account integration (connect / status / disconnect).
 *  2) Payment policy of the location (toggles + cancellation policy).
 */
@Component({
  selector: 'app-location-payments-section',
  templateUrl: './location-payments-section.component.html',
  styleUrls: ['./location-payments-section.component.css'],
  standalone: false
})
export class LocationPaymentsSectionComponent implements OnInit, OnDestroy {
  @Input() locationId!: string | null;

  status: StripeStatusDto | null = null;
  isLoading = false;
  isProcessing = false;

  // Field-initialised so the template can safely read `policyForm.get(...)`
  // during the very first change-detection cycle, even before the
  // constructor body has run (View Engine evaluates bindings eagerly).
  readonly policyForm: FormGroup = new FormGroup({
    acceptOnlinePayment: new FormControl(false),
    onlinePaymentRequired: new FormControl(false),
    acceptCashPayment: new FormControl(true),
    cancellationPolicy: new FormControl('', Validators.maxLength(2000))
  });

  isLoadingPolicy = false;
  isSavingPolicy = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly paymentsService: LocationPaymentsService,
    private readonly route: ActivatedRoute,
    private readonly notify: NotificationService
  ) {
    // When AcceptOnlinePayment turns off, OnlinePaymentRequired must follow.
    this.policyForm.get('acceptOnlinePayment')!.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: boolean) => {
        if (!value) {
          this.policyForm.get('onlinePaymentRequired')!.setValue(false, { emitEvent: false });
        }
      });
  }

  ngOnInit(): void {
    if (!this.locationId) {
      return;
    }

    const stripeFlag = this.route.snapshot.queryParamMap.get('stripe');
    if (stripeFlag === 'ok' || stripeFlag === 'refresh') {
      this.refreshFromStripe();
    } else {
      this.loadStatus();
    }

    this.loadPolicy();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ========== Stripe actions ==========

  connectStripe(): void {
    if (!this.locationId || this.isProcessing) {
      return;
    }
    this.isProcessing = true;

    const returnUrl = this.buildReturnUrl('ok');
    const refreshUrl = this.buildReturnUrl('refresh');

    this.paymentsService
      .connectStripe(this.locationId, { returnUrl, refreshUrl })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        result => {
          window.location.href = result.onboardingUrl;
        },
        error => {
          this.isProcessing = false;
          this.notify.errorBottom('No se ha podido iniciar la conexión con Stripe.');
          console.error('connectStripe error', error);
        }
      );
  }

  openDashboard(): void {
    if (!this.locationId || this.isProcessing) {
      return;
    }
    this.isProcessing = true;

    this.paymentsService
      .getStripeLoginLink(this.locationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        result => {
          this.isProcessing = false;
          window.open(result.url, '_blank');
        },
        error => {
          this.isProcessing = false;
          this.notify.errorBottom('No se ha podido abrir el dashboard de Stripe.');
          console.error('getStripeLoginLink error', error);
        }
      );
  }

  disconnect(): void {
    if (!this.locationId || this.isProcessing) {
      return;
    }
    const confirmed = window.confirm(
      'Vas a desconectar Stripe de este centro. Mientras esté desconectado no podrás aceptar pagos online. ¿Continuar?'
    );
    if (!confirmed) {
      return;
    }

    this.isProcessing = true;
    this.paymentsService
      .disconnectStripe(this.locationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.isProcessing = false;
          this.notify.okBottom('Stripe desconectado.');
          this.loadStatus();
          this.loadPolicy();
        },
        error => {
          this.isProcessing = false;
          this.notify.errorBottom('No se ha podido desconectar Stripe.');
          console.error('disconnectStripe error', error);
        }
      );
  }

  // ========== Policy actions ==========

  savePolicy(): void {
    if (!this.locationId || this.isSavingPolicy || this.policyForm.invalid) {
      return;
    }

    const value = this.policyForm.value as PaymentSettingsDto;

    if (!value.acceptOnlinePayment && !value.acceptCashPayment) {
      this.notify.errorBottom('El centro debe aceptar al menos un método de pago.');
      return;
    }

    this.isSavingPolicy = true;
    this.paymentsService
      .updatePaymentSettings(this.locationId, value)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        saved => {
          this.isSavingPolicy = false;
          this.policyForm.patchValue(saved, { emitEvent: false });
          this.notify.okBottom('Política de pagos guardada.');
        },
        error => {
          this.isSavingPolicy = false;
          const detail = this.extractServerError(error);
          this.notify.errorBottom(detail || 'No se ha podido guardar la política de pagos.');
          console.error('updatePaymentSettings error', error);
        }
      );
  }

  // ========== Status helpers ==========

  get isConnected(): boolean {
    return !!this.status && this.status.connected;
  }

  get isActive(): boolean {
    return !!this.status && this.status.status === 'active' && this.status.chargesEnabled;
  }

  get isPending(): boolean {
    return !!this.status && (this.status.status === 'pending' || this.status.status === 'restricted');
  }

  /** Whether the merchant can switch on AcceptOnlinePayment right now. */
  get canEnableOnline(): boolean {
    return this.isActive;
  }

  // ========== Private helpers ==========

  private loadStatus(): void {
    if (!this.locationId) {
      return;
    }
    this.isLoading = true;
    this.paymentsService
      .getStripeStatus(this.locationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        status => {
          this.status = status;
          this.isLoading = false;
        },
        error => {
          this.isLoading = false;
          this.notify.errorBottom('No se ha podido cargar el estado de Stripe.');
          console.error('getStripeStatus error', error);
        }
      );
  }

  private refreshFromStripe(): void {
    if (!this.locationId) {
      return;
    }
    this.isLoading = true;
    this.paymentsService
      .refreshStripeStatus(this.locationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        status => {
          this.status = status;
          this.isLoading = false;
        },
        error => {
          this.isLoading = false;
          this.loadStatus();
          console.error('refreshStripeStatus error', error);
        }
      );
  }

  private loadPolicy(): void {
    if (!this.locationId) {
      return;
    }
    this.isLoadingPolicy = true;
    this.paymentsService
      .getPaymentSettings(this.locationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        settings => {
          this.policyForm.patchValue(settings, { emitEvent: false });
          this.isLoadingPolicy = false;
        },
        error => {
          this.isLoadingPolicy = false;
          this.notify.errorBottom('No se ha podido cargar la política de pagos.');
          console.error('getPaymentSettings error', error);
        }
      );
  }

  /**
   * Walks the typical convfact-api error shape to extract a human-readable
   * message. Written without optional chaining for compatibility with the
   * older TS toolchain used by convfact-webapp.
   */
  private extractServerError(error: any): string | null {
    if (!error || !error.error) {
      return null;
    }
    const body = error.error;
    if (body.error && body.error.messages && body.error.messages.length > 0) {
      return body.error.messages[0];
    }
    if (body.detail) {
      return body.detail;
    }
    return null;
  }

  private buildReturnUrl(flag: 'ok' | 'refresh'): string {
    const url = new URL(window.location.href);
    url.searchParams.set('stripe', flag);
    return url.toString();
  }
}
